/**
 * BookingPriceViewModel → StickyBookingSummary bookingSummary display strings.
 * No React-side pricing math. FX labels respect pricingFxRate vs snapshot split.
 */

import { formatFlightReviewDisplayMoney } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveFlightReviewDisplayMoney";

/**
 * @param {import("../view-models/types").BookingPriceViewModel|null|undefined} priceVm
 * @param {{
 *   fare?: { cabin?: string|null, fareFamily?: string|null, fareType?: string|null }|null,
 *   baggageDisplay?: { chips?: { label: string, tone: string }[] }|null,
 *   passengersLabel?: string|null,
 * }} [ctx]
 */
export function mapDetailsBookingSummary(priceVm, ctx = {}) {
  if (!priceVm || typeof priceVm !== "object") return null;

  const paxCount = finite(priceVm.passengerPriceBreakdown?.totalPaxCount);
  const sourceCurrency = priceVm.sourceCurrency || null;
  const perPersonSource = resolvePerPersonSource(priceVm);
  // Customer transaction total. sourceCurrencyTotal is supplier/raw and must not be labeled as this row.
  const clientFlightTotal = finite(priceVm.clientFlightTotal);
  const perPersonLabel =
    perPersonSource != null && sourceCurrency
      ? formatMoney(perPersonSource, sourceCurrency)
      : null;

  const flightsLine =
    perPersonLabel != null || (clientFlightTotal != null && sourceCurrency)
      ? {
          title: "מחיר טיסות",
          lineLabel:
            perPersonLabel != null
              ? paxCount != null && paxCount > 1
                ? `${perPersonLabel} × ${paxCount} נוסעים`
                : perPersonLabel
              : "מחיר טיסות",
          amountLabel:
            clientFlightTotal != null && sourceCurrency
              ? formatMoney(clientFlightTotal, sourceCurrency)
              : "—",
        }
      : null;

  const baggageItems = mapBaggageSummaryItems(ctx.baggageDisplay);
  const exchangeRate = mapExchangeRateBlock(priceVm);
  const totals = mapTotals(priceVm);
  const commission = mapCommission(priceVm);
  const fare = ctx.fare
    ? {
        title: "Fare",
        family: ctx.fare.fareFamily || ctx.fare.fareType || null,
        cabin: ctx.fare.cabin || null,
      }
    : null;

  return {
    flights: flightsLine,
    baggage: baggageItems
      ? {
          title: "כבודה",
          items: baggageItems,
          extraLine: null,
          breakdown: null,
        }
      : null,
    extras: {
      title: "תוספות",
      emptyLabel: "תוספות",
      emptyAmountLabel: formatExtrasAmount(priceVm),
      items: [],
    },
    fare,
    sourceTotal:
      clientFlightTotal != null && sourceCurrency
        ? {
            title: "סה״כ במטבע העסקה",
            amountLabel: formatMoney(clientFlightTotal, sourceCurrency),
            currency: sourceCurrency,
          }
        : null,
    baggageUsdTotal: mapBaggageUsdTotal(priceVm),
    exchangeRate,
    totals,
    commission,
  };
}

/**
 * @param {import("../view-models/types").BookingPriceViewModel} priceVm
 */
export function mapExchangeRateBlock(priceVm) {
  const snapshot = finite(priceVm.bookingExchangeRateSnapshot);
  const pricingFx = finite(priceVm.pricingFxRate);
  const sourceCurrency = priceVm.sourceCurrency || null;

  if (snapshot != null) {
    return {
      title: "שער שנשמר להזמנה",
      rateLabel: formatFxRateLabel(sourceCurrency, snapshot),
      note: "שער ההמרה שנשמר עבור ההזמנה",
      mock: false,
      source: "bookingExchangeRateSnapshot",
    };
  }

  if (pricingFx != null) {
    return {
      title: "שער לחישוב",
      rateLabel: formatFxRateLabel(sourceCurrency, pricingFx),
      note: "שער לחישוב",
      mock: false,
      source: "pricingFxRate",
    };
  }

  return null;
}

function mapTotals(priceVm) {
  const calculated = finite(priceVm.calculatedIlsTotal);
  const payable = finite(
    priceVm.summaryPayableIlsTotal != null
      ? priceVm.summaryPayableIlsTotal
      : priceVm.payableIlsTotal
  );

  const baggageIls = mapBaggageIlsAmount(priceVm);

  if (calculated == null && payable == null && baggageIls == null) return null;

  return {
    calculatedLabel: "סה״כ בשקלים",
    calculatedTotalLabel:
      calculated != null ? formatMoney(calculated, "ILS") : "—",
    roundingLabel: "עיגול",
    roundingAdjustmentLabel:
      priceVm.roundingAdjustment != null
        ? formatMoney(priceVm.roundingAdjustment, "ILS")
        : null,
    baggageIlsLabel: baggageIls != null ? "כבודה בשקלים" : null,
    baggageIlsAmountLabel: baggageIls,
    payableLabel: "לתשלום",
    payableTotalLabel: formatPayableDisplay(payable, finite(priceVm.pricingFxRate)),
  };
}

function mapBaggageIlsAmount(priceVm) {
  const ils = finite(priceVm.extrasTotal);
  if (ils == null || ils <= 0) return null;
  const currency = String(priceVm.extrasCurrency || "ILS").trim().toUpperCase();
  if (currency !== "ILS" && currency !== "₪" && currency !== "NIS") return null;
  return formatMoney(ils, "ILS");
}

function formatPayableDisplay(payableIls, rate) {
  if (payableIls == null) return "—";
  const ilsLabel = formatMoney(payableIls, "ILS");
  if (!ilsLabel) return "—";
  if (rate == null || rate <= 0) return ilsLabel;
  const usdLabel = formatMoney(payableIls / rate, "USD");
  if (!usdLabel) return ilsLabel;
  return `${usdLabel} (${ilsLabel})`;
}

function mapCommission(priceVm) {
  const fee = finite(priceVm.handlingFee);
  if (fee == null || fee === 0) return null;
  return {
    label: "עמלת סוכן",
    valueLabel: formatMoney(fee, "ILS"),
  };
}

function mapBaggageSummaryItems(baggageDisplay) {
  const chips = baggageDisplay?.chips || [];
  if (!chips.length) return null;
  return chips.map((c) => ({
    label: c.label,
    secondary: null,
    valueLabel: c.tone === "paid" ? "—" : "כלול",
    tone: c.tone === "included" ? "default" : c.tone === "paid" ? "muted" : "muted",
    ltr: false,
  }));
}

function resolvePerPersonSource(priceVm) {
  const bd = priceVm.passengerPriceBreakdown;
  if (!bd) return null;
  const c = String(priceVm.sourceCurrency || "").trim().toUpperCase();
  if (c === "USD" || c === "$" || c === "US$") return finite(bd.perPersonUsd);
  if (c === "ILS" || c === "₪" || c === "NIS") return finite(bd.perPersonIls);
  return null;
}

function mapBaggageUsdTotal(priceVm) {
  const ils = finite(priceVm.extrasTotal);
  const rate = finite(priceVm.pricingFxRate);
  if (ils == null || ils <= 0 || rate == null || rate <= 0) return null;
  const currency = String(priceVm.extrasCurrency || "ILS").trim().toUpperCase();
  if (currency !== "ILS" && currency !== "₪" && currency !== "NIS") return null;
  const amountLabel = formatMoney(ils / rate, "USD");
  if (!amountLabel || amountLabel === formatMoney(0, "USD")) return null;
  return {
    title: "סה״כ כבודה בדולר",
    amountLabel,
  };
}

function formatExtrasAmount(priceVm) {
  const amount = finite(priceVm.extrasTotal);
  const currency = priceVm.extrasCurrency || null;
  if (amount == null || !currency) return null;
  return formatMoney(amount, currency);
}

function formatFxRateLabel(currency, rate) {
  const c = String(currency || "").trim().toUpperCase();
  const sym = c === "ILS" || c === "₪" || c === "NIS" ? "₪" : c === "USD" || c === "$" || c === "US$" ? "$" : c;
  const right = `₪${Number(rate).toFixed(4)}`;
  if (!sym) return right;
  return `${sym}1 = ${right}`;
}

function formatMoney(amount, currency) {
  return formatFlightReviewDisplayMoney(amount, currency);
}

function finite(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
